/**
 * MCP tools: the design-template surface (A3). yap_start_design executes a
 * template's element specs through the regular create-element handler, so
 * every payload goes through the same builders and contract errors an agent
 * would hit by hand.
 */

import { DESIGN_TEMPLATES, findDesignTemplate, type DesignTemplateElement } from './designTemplates';
import { handleCreateElement } from './mcpSceneTools';
import { useBIMStore } from '../../state/useBIMStore';
import type { McpToolHandler } from './mcpResultUtils';
import { asString, fail, okJson } from './mcpResultUtils';

export const handleListDesignTemplates: McpToolHandler = () => {
    return okJson(
        {
            templates: DESIGN_TEMPLATES.map((t) => ({
                id: t.id,
                label: t.label,
                archetype: t.archetype,
                description: t.description,
                elementCount: t.elements.length,
            })),
        },
        `${DESIGN_TEMPLATES.length} design template(s) available. Start one with yap_start_design.`,
    );
};

/** Execute one spec, resolving template-local wall NAME references to ids. */
function executeSpec(spec: DesignTemplateElement, nameToId: Map<string, string>) {
    const hostWallId = typeof spec.hostWallId === 'string' ? spec.hostWallId : undefined;
    const args = { ...spec } as Record<string, unknown>;
    if (hostWallId && !nameToId.has(hostWallId)) {
        return fail(`Template references unknown wall '${hostWallId}'.`);
    }
    if (hostWallId) args.hostWallId = nameToId.get(hostWallId);
    return handleCreateElement(args);
}

export const handleStartDesign: McpToolHandler = async (args) => {
    const templateId = asString(args.template);
    if (!templateId) {
        return fail("Missing required argument 'template' — pick one from yap_list_design_templates (e.g. 'single-story-house').");
    }
    const template = findDesignTemplate(templateId);
    if (!template) {
        return fail(`No design template '${templateId}'. Available: ${DESIGN_TEMPLATES.map((t) => t.id).join(', ')}.`);
    }

    const nameToId = new Map<string, string>();
    const created: { name: string; category: string; id: string }[] = [];
    const failures: { name: string; error: string }[] = [];
    for (const spec of template.elements) {
        // A template assumes a fresh document, but the app seeds a default
        // level ("Level 1" @ 0 mm). Reuse it instead of failing the whole run.
        if (spec.category === 'Levels') {
            const existing = Array.from(useBIMStore.getState().project.levels.values()).find(
                (l) =>
                    l.name.toLowerCase() === (spec.name ?? '').toLowerCase() ||
                    (spec.elevation !== undefined &&
                        typeof spec.elevation === 'number' &&
                        Math.abs(l.elevation * 1000 - spec.elevation) < 0.5),
            );
            if (existing) {
                if (spec.name) nameToId.set(spec.name, existing.id);
                nameToId.set(`level:${spec.name}`, existing.id);
                created.push({ name: `${spec.name} (reused existing)`, category: spec.category, id: existing.id });
                continue;
            }
        }
        const result = await executeSpec(spec, nameToId);
        const label = spec.name ?? spec.category;
        if (result.ok && result.data && typeof result.data === 'object' && 'id' in result.data) {
            const id = (result.data as { id: string }).id;
            if (spec.name) nameToId.set(spec.name, id);
            if (spec.category === 'Levels') nameToId.set(`level:${spec.name}`, id);
            created.push({ name: label, category: spec.category, id });
        } else if (result.ok) {
            // Levels commit through the kernel, not the element map.
            created.push({ name: label, category: spec.category, id: (result.data as { id?: string })?.id ?? '' });
        } else {
            failures.push({ name: label, error: result.error ?? 'unknown error' });
            break;
        }
    }

    if (failures.length > 0) {
        return fail(
            `Template '${template.id}' stopped after ${created.length}/${template.elements.length} elements. ` +
                `First failure at '${failures[0]?.name}': ${failures[0]?.error}`,
        );
    }
    return okJson(
        { template: template.id, created, summary: template.description },
        `Started design from template '${template.label}': ${created.length} element(s). ` +
            'Continue the playbook phases (partitions, openings, interiors, annotations) and verify with yap_validate_design.',
    );
};
